import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";
import { setConsent, hasDecision } from "../../lib/consent";

export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    // Small delay so it doesn't compete with hero animations
    const id = setTimeout(() => { if (!hasDecision()) setVisible(true); }, 1200);
    return () => clearTimeout(id);
  }, []);

  if (!visible) return null;

  const acceptAll = () => { setConsent({ analytics: true, ads: true }); setVisible(false); };
  const rejectAll = () => { setConsent({ analytics: false, ads: false }); setVisible(false); };
  const savePrefs = () => { setConsent({ analytics, ads }); setVisible(false); };

  return (
    <div
      data-testid="consent-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:bottom-6 md:right-auto md:max-w-md z-[60] animate-fade-in"
      role="dialog"
      aria-live="polite"
      aria-label="Preferências de cookies"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <span className="shrink-0 w-9 h-9 rounded-full bg-gold-50 text-gold-600 inline-flex items-center justify-center mt-0.5">
            <Cookie size={18} strokeWidth={1.8} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-serif text-brand-500 text-lg leading-tight">Cookies & Privacidade</h2>
            <p className="text-sm text-slate-600 leading-relaxed mt-1.5">
              Usamos cookies essenciais e, com a sua autorização, cookies analíticos para melhorar o site.
              Pode aceitar tudo, recusar ou personalizar.{" "}
              <Link to="/politica-privacidade" className="text-gold-600 hover:underline">Saber mais</Link>.
            </p>
          </div>
          <button
            data-testid="consent-close"
            onClick={rejectAll}
            aria-label="Fechar e recusar"
            className="shrink-0 -mr-1 -mt-1 w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition"
          ><X size={16} /></button>
        </div>

        {open && (
          <div className="px-5 pb-3 space-y-2" data-testid="consent-prefs">
            <label className="flex items-center justify-between gap-3 py-2 border-t border-slate-100">
              <span>
                <span className="block text-sm font-semibold text-brand-500">Essenciais</span>
                <span className="block text-xs text-slate-500">Necessários ao funcionamento — sempre ativos.</span>
              </span>
              <input type="checkbox" checked disabled className="w-4 h-4 accent-gold-500 opacity-60" />
            </label>
            <label className="flex items-center justify-between gap-3 py-2 border-t border-slate-100">
              <span>
                <span className="block text-sm font-semibold text-brand-500">Analíticos</span>
                <span className="block text-xs text-slate-500">Google Analytics — ajuda-nos a melhorar o site.</span>
              </span>
              <input
                type="checkbox"
                data-testid="consent-toggle-analytics"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="w-4 h-4 accent-gold-500"
              />
            </label>
            <label className="flex items-center justify-between gap-3 py-2 border-t border-slate-100">
              <span>
                <span className="block text-sm font-semibold text-brand-500">Marketing</span>
                <span className="block text-xs text-slate-500">Permite mostrar anúncios mais relevantes.</span>
              </span>
              <input
                type="checkbox"
                data-testid="consent-toggle-ads"
                checked={ads}
                onChange={(e) => setAds(e.target.checked)}
                className="w-4 h-4 accent-gold-500"
              />
            </label>
          </div>
        )}

        <div className="px-5 pb-5 pt-1 flex flex-col sm:flex-row gap-2">
          {!open ? (
            <>
              <button
                data-testid="consent-customize"
                onClick={() => setOpen(true)}
                className="flex-1 px-3 py-2 text-sm font-semibold text-brand-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
              >Personalizar</button>
              <button
                data-testid="consent-reject"
                onClick={rejectAll}
                className="flex-1 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
              >Recusar</button>
              <button
                data-testid="consent-accept"
                onClick={acceptAll}
                className="flex-1 px-3 py-2 text-sm font-semibold text-brand-700 bg-gold-400 hover:bg-gold-500 rounded-lg transition shadow"
              >Aceitar tudo</button>
            </>
          ) : (
            <>
              <button
                data-testid="consent-back"
                onClick={() => setOpen(false)}
                className="flex-1 px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
              >Voltar</button>
              <button
                data-testid="consent-save"
                onClick={savePrefs}
                className="flex-1 px-3 py-2 text-sm font-semibold text-brand-700 bg-gold-400 hover:bg-gold-500 rounded-lg transition shadow"
              >Guardar preferências</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
